import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Header from '../components/Header'
import { CRIME_TYPES } from '../lib/types'
import { getStateDisplayName } from '../lib/stateLabels'

type ViewMode = 'state' | 'municipality'

interface RankingItem {
  rank: number
  id: string
  name: string
  state?: string
  count: number
  rate: number
  population: number
}

type CrimeDataset = {
  states: Array<{ id: string; name: string; population: number; crimes: Record<string, number>; total: number; rate: number }>
}

type MunicipalPointsGeoJSON = {
  type: 'FeatureCollection'
  features: Array<{ properties: any }>
}

export default function Rankings() {
  const [viewMode, setViewMode] = useState<ViewMode>('state')
  const [selectedCrimeType, setSelectedCrimeType] = useState<string>('all')
  const [sortBy, setSortBy] = useState<'count' | 'rate'>('count')
  const [rankings, setRankings] = useState<RankingItem[]>([])

  useEffect(() => {
    const fetchRankings = async () => {
      try {
        const params = new URLSearchParams({
          view: viewMode,
          crimeType: selectedCrimeType,
          sortBy,
        })
        const response = await fetch(`/api/rankings?${params}`)
        if (response.ok) {
          const data = await response.json()
          setRankings(data)
          return
        }

        // Local (Vite) fallback: derive from static files.
        const [crimeRes, muniRes] = await Promise.all([
          fetch('/data/crime-data.json'),
          fetch('/data/municipal-points.geojson'),
        ])
        if (!crimeRes.ok) return
        const crime = (await crimeRes.json()) as CrimeDataset
        const muni = muniRes.ok ? ((await muniRes.json()) as MunicipalPointsGeoJSON) : null

        const makeStateRankings = () => {
          let items = crime.states.map((s) => {
            const count =
              selectedCrimeType === 'all' ? s.total : Number(s.crimes[selectedCrimeType] || 0)
            const rate = s.population > 0 ? (count / s.population) * 100000 : 0
            return {
              rank: 0,
              id: s.name,
              name: getStateDisplayName(s.name),
              count,
              rate: Math.round(rate * 10) / 10,
              population: s.population,
            } satisfies RankingItem
          })
          items.sort((a, b) => (sortBy === 'rate' ? b.rate - a.rate : b.count - a.count))
          return items.map((r, i) => ({ ...r, rank: i + 1 }))
        }

        const makeMunicipalityRankings = () => {
          const features = muni?.features || []
          let items = features
            .map((f: any) => f.properties)
            .map((p: any) => {
              const count =
                selectedCrimeType === 'all' ? (p.totalCrimes || 0) : (p[selectedCrimeType] || 0)
              const pop = p.population || 0
              const rate = pop > 0 ? (count / pop) * 100000 : 0
              return {
                rank: 0,
                id: p.id,
                name: p.name,
                state: getStateDisplayName(p.state),
                count,
                rate: Math.round(rate * 10) / 10,
                population: pop,
              } satisfies RankingItem
            })
            .filter((r: RankingItem) => r.count > 0)

          items.sort((a, b) => (sortBy === 'rate' ? b.rate - a.rate : b.count - a.count))
          return items.map((r, i) => ({ ...r, rank: i + 1 }))
        }

        setRankings(viewMode === 'state' ? makeStateRankings() : makeMunicipalityRankings())
      } catch (error) {
        console.error('Failed to fetch rankings:', error)
      }
    }

    fetchRankings()
  }, [viewMode, selectedCrimeType, sortBy])

  const maxCount = Math.max(...rankings.map((r) => r.count), 1)

  return (
    <div className="min-h-screen bg-[var(--color-bg-dark)]">
      <Header onSearch={() => {}} />

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-[var(--color-text)]">Rankings</h1>
          <p className="text-[var(--color-text-muted)] mt-2">
            Tablas de posiciones y rankings de incidencia delictiva
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          {/* Crime Type Selector */}
          <div>
            <label className="text-xs text-[var(--color-text-muted)] block mb-1">
              Tipo de Delito
            </label>
            <select
              value={selectedCrimeType}
              onChange={(e) => setSelectedCrimeType(e.target.value)}
              className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg px-4 py-2 text-[var(--color-text)] focus:outline-none focus:border-[var(--color-primary)]"
            >
              <option value="all">Todos los delitos</option>
              {Object.entries(CRIME_TYPES).map(([key, crime]) => (
                <option key={key} value={key}>
                  {crime.nameEs}
                </option>
              ))}
            </select>
          </div>

          {/* View Mode Toggle */}
          <div>
            <label className="text-xs text-[var(--color-text-muted)] block mb-1">
              Agrupar por
            </label>
            <div className="flex rounded-lg overflow-hidden border border-[var(--color-border)]">
              <button
                onClick={() => setViewMode('state')}
                className={`px-4 py-2 text-sm transition-colors ${
                  viewMode === 'state'
                    ? 'bg-[var(--color-primary)] text-black'
                    : 'bg-[var(--color-bg-card)] text-[var(--color-text)] hover:bg-[var(--color-border)]'
                }`}
              >
                Por Estado
              </button>
              <button
                onClick={() => setViewMode('municipality')}
                className={`px-4 py-2 text-sm transition-colors ${
                  viewMode === 'municipality'
                    ? 'bg-[var(--color-primary)] text-black'
                    : 'bg-[var(--color-bg-card)] text-[var(--color-text)] hover:bg-[var(--color-border)]'
                }`}
              >
                Por Municipio
              </button>
            </div>
          </div>

          {/* Sort Toggle */}
          <div>
            <label className="text-xs text-[var(--color-text-muted)] block mb-1">
              Ordenar por
            </label>
            <div className="flex rounded-lg overflow-hidden border border-[var(--color-border)]">
              <button
                onClick={() => setSortBy('count')}
                className={`px-4 py-2 text-sm transition-colors ${
                  sortBy === 'count'
                    ? 'bg-[var(--color-primary)] text-black'
                    : 'bg-[var(--color-bg-card)] text-[var(--color-text)] hover:bg-[var(--color-border)]'
                }`}
              >
                Total
              </button>
              <button
                onClick={() => setSortBy('rate')}
                className={`px-4 py-2 text-sm transition-colors ${
                  sortBy === 'rate'
                    ? 'bg-[var(--color-primary)] text-black'
                    : 'bg-[var(--color-bg-card)] text-[var(--color-text)] hover:bg-[var(--color-border)]'
                }`}
              >
                Tasa /100k
              </button>
            </div>
          </div>
        </div>

        {/* Rankings Table */}
        <div className="bg-[var(--color-bg-card)] rounded-xl border border-[var(--color-border)] overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg-dark)]">
                <th className="p-4 text-left text-xs text-[var(--color-text-muted)] uppercase tracking-wide w-16">
                  Rank
                </th>
                <th className="p-4 text-left text-xs text-[var(--color-text-muted)] uppercase tracking-wide">
                  {viewMode === 'state' ? 'Estado' : 'Municipio'}
                </th>
                {viewMode === 'municipality' && (
                  <th className="p-4 text-left text-xs text-[var(--color-text-muted)] uppercase tracking-wide hidden sm:table-cell">
                    Estado
                  </th>
                )}
                <th className="p-4 text-xs text-[var(--color-text-muted)] uppercase tracking-wide w-1/3">
                  Distribución
                </th>
                <th className="p-4 text-right text-xs text-[var(--color-text-muted)] uppercase tracking-wide">
                  {sortBy === 'count' ? 'Total' : 'Tasa'}
                </th>
              </tr>
            </thead>
            <tbody>
              {rankings.map((item) => (
                <tr
                  key={item.id}
                  className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-dark)] transition-colors"
                >
                  <td className="p-4">
                    <span className="text-[var(--color-primary)] font-bold">
                      {item.rank}
                    </span>
                  </td>
                  <td className="p-4">
                    <Link
                      to={`/?${viewMode === 'state' ? 'state' : 'municipality'}=${encodeURIComponent(
                        item.id
                      )}`}
                      className="text-[var(--color-text)] font-medium hover:text-[var(--color-primary)] transition-colors"
                    >
                      {item.name}
                    </Link>
                  </td>
                  {viewMode === 'municipality' && (
                    <td className="p-4 text-[var(--color-text-muted)] hidden sm:table-cell">
                      {item.state}
                    </td>
                  )}
                  <td className="p-4">
                    <div className="h-4 bg-[var(--color-bg-dark)] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[var(--color-primary)] rounded-full transition-all duration-500"
                        style={{
                          width: `${(item.count / maxCount) * 100}%`,
                        }}
                      />
                    </div>
                  </td>
                  <td className="p-4 text-right">
                    <span className="text-[var(--color-primary)] font-medium">
                      {sortBy === 'count'
                        ? item.count.toLocaleString()
                        : item.rate.toFixed(1)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <footer className="mt-12 text-center text-sm text-[var(--color-text-muted)]">
          <p>
            Este sitio muestra datos estadísticos derivados con fines educativos y de investigación.
            Los datos provienen del SESNSP e INEGI.{' '}
            <Link to="/methodology" className="text-[var(--color-primary)] hover:underline">
              Ver metodología completa
            </Link>
          </p>
        </footer>
      </main>
    </div>
  )
}
